import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import Sparkline from './Sparkline';

// "Why": everything behind one observation, pulled on demand (2026-10 restyle).
// In the order a careful person checks things: what moved (each mover with
// its own small chart), what moved alongside (coincidences, never causes),
// whether the measurement itself changed, and finally the question only you
// can answer: what was going on? One-tap chips, an optional line of text.
// Answering or setting aside closes the card; the answer is kept.
//
// `WhyBody` is the content. The Insights feed renders it inline under a card;
// `WhyDrawer` wraps it in a dialog for the Home page.

const H4 = ({ children }) => <h4 className="mb-2 text-sm font-medium text-fg-3">{children}</h4>;

const fmt = (v, unit) => {
  if (v == null) return '—';
  const n = Math.abs(v) >= 20 || Number.isInteger(v) ? Math.round(v) : Math.round(v * 10) / 10;
  if (!unit) return `${n}`;
  if (unit === '%' || unit === 'Δ' || unit.startsWith('/')) return `${n}${unit}`;
  return `${n} ${unit}`;
};

const Mover = ({ m, valence }) => (
  <div className="rounded-xl border border-line px-3.5 py-3">
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-base font-medium">{m.label}</span>
      <span className="text-sm text-fg-2">
        {m.baseline_text || fmt(m.baseline, m.unit)} → {m.value_text || fmt(m.recent ?? m.value, m.unit)}
      </span>
    </div>
    <div className="mt-2"><Sparkline chart={m.chart} valence={valence} height={56} quiet /></div>
    {m.z != null && (
      <p className="mt-1.5 text-sm text-fg-3">
        {Math.abs(m.z).toFixed(1)}× your normal day-to-day swing{m.n_baseline ? ` · judged against ${m.n_baseline} readings` : ''}
      </p>
    )}
  </div>
);

export const useWhy = (id, open = true) => {
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!open || !id) return undefined;
    let alive = true;
    setDetail(null); setFailed(false);
    apiClient.get(`/api/observations/${encodeURIComponent(id)}`)
      .then((res) => {
        if (!alive) return;
        setDetail(res.data);
        if (res.data?.status === 'unseen') apiClient.post(`/api/observations/${encodeURIComponent(id)}/seen`).catch(() => {});
      })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; };
  }, [id, open]);
  return { detail, failed };
};

export const WhyBody = ({ id, detail, failed, onResolved, onClose, onAsk, showHead = false }) => {
  const [choice, setChoice] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setChoice(null); setText(''); }, [id]);

  const submit = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/observations/${encodeURIComponent(id)}/answer`, { choice, text: text.trim() || null });
      toast.success('Noted. Thanks.');
      onResolved?.(id, 'answered');
      onClose?.();
    } catch { toast.error('Could not save that.'); } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/api/observations/${encodeURIComponent(id)}/dismiss`);
      onResolved?.(id, 'dismissed');
      onClose?.();
    } catch { toast.error('Could not set that aside.'); } finally { setBusy(false); }
  };

  if (failed) return <p className="text-fg-3">Could not load this one.</p>;
  if (!detail) return <div className="h-24 animate-pulse rounded-xl border border-line" />;

  const why = detail.why;
  const chips = [...(why?.co_movers || []), ...(why?.prompts || [])];
  const whenWord = detail.kind === 'today' ? 'that day' : detail.kind === 'stack' ? 'since you started' : 'then';

  return (
    <div className="grid gap-4">
      {showHead && (
        <div>
          <p className="text-sm font-medium text-fg-3">{detail.kind_label}</p>
          <p className="mt-1 text-[1.15rem] font-medium leading-[1.3] tracking-[-0.01em]">{detail.headline}</p>
          {detail.subline && <p className="mt-1.5 text-fg-2">{detail.subline}</p>}
        </div>
      )}

      {why?.movers?.length > 0 && (
        <div>
          <H4>What moved</H4>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {why.movers.map((m) => <Mover key={m.key} m={m} valence={detail.valence} />)}
          </div>
        </div>
      )}

      {detail.kind === 'tendency' && why?.pattern && (
        <div>
          <H4>The pattern</H4>
          <div className="rounded-xl border border-line px-3.5 py-3">
            <Sparkline chart={detail.chart} quiet />
            <p className="mt-2 text-sm text-fg-3">
              {why.pattern.days} days compared · {why.pattern.n_high} with more {why.pattern.input.toLowerCase()}, {why.pattern.n_low} with less
              {why.pattern.forward_result === 'held' ? ' · still holding in the weeks since' : ''}
            </p>
          </div>
        </div>
      )}

      <div>
        <H4>What else moved {whenWord}</H4>
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <div className="rounded-xl border border-line px-3.5 py-3">
            <H4>Moved with it</H4>
            {why?.co_movers?.length ? (
              <ul className="grid gap-1.5">
                {why.co_movers.map((g) => (
                  <li key={g.key} title={g.rationale ? `Why we watch this pair: ${g.rationale}` : undefined}
                    className="flex items-start gap-2.5 text-sm text-fg-2">
                    <i className="mt-[7px] h-2 w-2 flex-none rounded-full bg-good-strong dark:bg-good" />
                    <span>{g.text.replace(/\s—\s/g, ': ')}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-fg-3">Nothing we track moved unusually with it.</p>}
          </div>
          {why?.measurement && (
            <div className="rounded-xl border border-line px-3.5 py-3 sm:max-w-[260px]">
              <H4>The measurement itself</H4>
              <p className={`text-sm ${why.measurement.flag ? 'font-medium text-fg' : 'text-fg-2'}`}>{why.measurement.text}</p>
            </div>
          )}
        </div>
        <p className="mt-2 text-sm text-fg-3">Things that moved together, not things that caused each other.</p>
      </div>

      {detail.can_answer && (
        <div>
          <H4>Anything going on {whenWord}?</H4>
          <div className="flex flex-wrap items-center gap-2">
            {chips.map((g) => (
              <button key={g.key} type="button" onClick={() => setChoice(g.key)} aria-pressed={choice === g.key}
                className={`rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
                  choice === g.key ? 'border-fg-3 bg-elev text-fg' : 'border-line-strong text-fg-2 hover:bg-elev hover:text-fg'}`}>
                {g.label}
              </button>
            ))}
            {detail.can_dismiss && (
              <button type="button" onClick={dismiss} disabled={busy} className="ml-auto bg-transparent p-0 text-sm font-medium text-fg-3 hover:text-fg">Not now</button>
            )}
          </div>
          {choice && (
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Anything else worth noting? Optional."
                className="min-w-[240px] flex-1 rounded-xl border border-line-strong bg-transparent p-3 text-base text-fg placeholder:text-fg-3 focus:border-fg-3 focus:outline-none" />
              <button type="button" onClick={submit} disabled={busy}
                className="rounded-full bg-cta px-[22px] py-3 text-base font-medium text-cta-fg hover:bg-cta-hover disabled:opacity-50">Save</button>
            </div>
          )}
        </div>
      )}

      {detail.status === 'answered' && detail.answer_choice && (
        <p className="text-sm text-fg-2">You said: {detail.answer_choice.replace(/_/g, ' ')}{detail.answer_text ? `. ${detail.answer_text}` : ''}</p>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-fg-3">
        {onAsk && (
          <button type="button" onClick={() => onAsk(detail)} className="bg-transparent p-0 font-medium text-fg-2 hover:text-fg">Ask about this →</button>
        )}
        {why?.method && <span>How we judged it: {why.method}</span>}
      </div>
      <p className="text-sm text-fg-3">
        {detail.disclaimer || 'A change in your own measurements, not a diagnosis. If something feels wrong or this keeps up, talk to a clinician.'}
      </p>
    </div>
  );
};

const WhyDrawer = ({ id, open, onClose, onResolved, onAsk }) => {
  const { detail, failed } = useWhy(id, open);
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-4">
        <Dialog.Panel className="max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl border border-line bg-raised p-5 sm:max-w-2xl sm:rounded-2xl sm:p-6">
          <div className="mb-4 flex items-start justify-between gap-3">
            <Dialog.Title className="text-sm font-medium text-fg-3">Why</Dialog.Title>
            <button type="button" onClick={onClose} aria-label="Close"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-transparent text-fg-2 hover:bg-elev hover:text-fg">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <WhyBody id={id} detail={detail} failed={failed} onResolved={onResolved} onClose={onClose} onAsk={onAsk} showHead />
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default WhyDrawer;
